import { sanitizeRichText } from "@/lib/sanitize";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApi } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft } from "lucide-react";

export default function AdminTemplatesPage() {
  const api = useApi();
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api("/templates");
      if (r.ok) { const d = await r.json(); setTemplates(d.templates || []); }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    const r = await api(`/templates/${id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Template deleted"); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Delete failed"); }
  };

  return (
    <div>
      <button onClick={() => nav("/admin")} className="text-sm text-primary inline-flex items-center gap-1 mb-4">
        <ChevronLeft size={16} /> Back to Admin
      </button>
      <PageHeader
        title="Event templates"
        subtitle="Pick a template to create a new event, or manage the library."
        actions={<Link to="/admin/template-new"><Button data-testid="new-template"><Plus size={16} className="mr-1" /> New template</Button></Link>}
      />
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> :
        templates.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="font-semibold">No templates yet</div>
            <p className="text-sm text-muted-foreground mt-1">Create a template to reuse fields for new events.</p>
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {templates.map((t) => (
              <Card key={t.template_id} className="p-4 flex items-start justify-between gap-3" data-testid={`template-${t.template_id}`}>
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-1" dangerouslySetInnerHTML={{ __html: sanitizeRichText(t.description) }} />
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Link to={`/admin/events/new?template=${t.template_id}`}>
                    <Button size="sm">Create event</Button>
                  </Link>
                  <Link to={`/admin/templates/${t.template_id}/edit`}>
                    <Button size="sm" variant="outline">Edit</Button>
                  </Link>
                  <Button size="sm" variant="ghost" onClick={() => del(t.template_id)} data-testid={`template-delete-${t.template_id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
